<template>
  <div class="schedule">
    <!-- 主要内容区 -->
    <main class="max-w-7xl mx-auto px-4 py-8">
      <!-- 排期数据概览 -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-blue-100 text-blue-600">
              <i class="fas fa-calendar-alt text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-gray-500">总排期</p>
              <p class="text-2xl font-bold">{{ scheduleData.totalSchedules }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-green-100 text-green-600">
              <i class="fas fa-play text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-gray-500">今日直播</p>
              <p class="text-2xl font-bold">{{ scheduleData.todayLive }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-yellow-100 text-yellow-600">
              <i class="fas fa-clock text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-gray-500">待审核</p>
              <p class="text-2xl font-bold">{{ scheduleData.pendingReview }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-purple-100 text-purple-600">
              <i class="fas fa-users text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-gray-500">参与主播</p>
              <p class="text-2xl font-bold">{{ scheduleData.participatingStreamers }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 日历视图 -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-8">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-bold">排期日历</h2>
          <div class="flex space-x-2">
            <button class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              <i class="fas fa-plus mr-2"></i>新建排期
            </button>
            <button class="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200">
              <i class="fas fa-filter mr-2"></i>筛选
            </button>
          </div>
        </div>
        
        <!-- 日历网格 -->
        <div class="grid grid-cols-7 gap-1">
          <!-- 星期标题 -->
          <div class="p-3 text-center font-medium text-gray-500">周日</div>
          <div class="p-3 text-center font-medium text-gray-500">周一</div>
          <div class="p-3 text-center font-medium text-gray-500">周二</div>
          <div class="p-3 text-center font-medium text-gray-500">周三</div>
          <div class="p-3 text-center font-medium text-gray-500">周四</div>
          <div class="p-3 text-center font-medium text-gray-500">周五</div>
          <div class="p-3 text-center font-medium text-gray-500">周六</div>
          
          <!-- 日期单元格 -->
          <div 
            v-for="day in calendarDays" 
            :key="day.date"
            class="min-h-24 p-2 border border-gray-200 hover:bg-gray-50"
          >
            <div class="text-sm font-medium mb-1">{{ day.dayNumber }}</div>
            <div class="space-y-1">
              <div 
                v-for="schedule in day.schedules" 
                :key="schedule.id"
                :class="[
                  'text-xs p-1 rounded cursor-pointer',
                  schedule.type === 'live' ? 'bg-red-100 text-red-800' :
                  schedule.type === 'content' ? 'bg-blue-100 text-blue-800' :
                  'bg-green-100 text-green-800'
                ]"
              >
                {{ schedule.title }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 排期列表 -->
      <div class="bg-white rounded-lg shadow-md overflow-hidden">
        <div class="px-6 py-4 border-b">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-bold">排期列表</h2>
            <div class="flex space-x-2">
              <select class="border rounded-lg px-3 py-1 text-sm">
                <option>全部状态</option>
                <option>已确认</option>
                <option>待审核</option>
                <option>已取消</option>
              </select>
              <select class="border rounded-lg px-3 py-1 text-sm">
                <option>全部类型</option>
                <option>直播</option>
                <option>内容</option>
                <option>活动</option>
              </select>
            </div>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">排期ID</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">标题</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">主播</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">时间</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr 
                v-for="schedule in scheduleList" 
                :key="schedule.id"
                class="hover:bg-gray-50"
              >
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ schedule.id }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div class="text-sm font-medium text-gray-900">{{ schedule.title }}</div>
                    <div class="text-sm text-gray-500">{{ schedule.description }}</div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <img class="h-8 w-8 rounded-full" :src="schedule.streamerAvatar" :alt="schedule.streamerName">
                    <div class="ml-3">
                      <div class="text-sm font-medium text-gray-900">{{ schedule.streamerName }}</div>
                      <div class="text-sm text-gray-500">{{ schedule.streamerId }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span 
                    :class="[
                      'px-2 py-1 text-xs rounded-full',
                      schedule.type === 'live' ? 'bg-red-100 text-red-800' :
                      schedule.type === 'content' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    ]"
                  >
                    {{ getTypeText(schedule.type) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div>{{ schedule.date }}</div>
                  <div class="text-gray-500">{{ schedule.time }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span 
                    :class="[
                      'px-2 py-1 text-xs rounded-full',
                      schedule.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                      schedule.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    ]"
                  >
                    {{ getStatusText(schedule.status) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex space-x-2">
                    <button class="text-blue-600 hover:text-blue-900">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="text-green-600 hover:text-green-900">
                      <i class="fas fa-play"></i>
                    </button>
                    <button class="text-red-600 hover:text-red-900">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'SchedulePage',
  data() {
    return {
      scheduleData: {
        totalSchedules: 156,
        todayLive: 12,
        pendingReview: 8,
        participatingStreamers: 45
      },
      calendarDays: [
        {
          date: '2024-01-15',
          dayNumber: 15,
          schedules: [
            {
              id: '1',
              title: '美食直播',
              type: 'live'
            },
            {
              id: '2',
              title: '内容创作',
              type: 'content'
            }
          ]
        },
        {
          date: '2024-01-16',
          dayNumber: 16,
          schedules: [
            {
              id: '3',
              title: '美妆分享',
              type: 'live'
            }
          ]
        },
        {
          date: '2024-01-17',
          dayNumber: 17,
          schedules: []
        }
      ],
      scheduleList: [
        {
          id: 'SCH001',
          title: '美食探店直播',
          description: '探店网红餐厅，分享美食体验',
          streamerName: '小美',
          streamerId: 'U001',
          streamerAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
          type: 'live',
          date: '2024-01-15',
          time: '14:00-16:00',
          status: 'confirmed'
        },
        {
          id: 'SCH002',
          title: '美妆教程分享',
          description: '分享日常妆容技巧',
          streamerName: '小丽',
          streamerId: 'U002',
          streamerAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
          type: 'live',
          date: '2024-01-16',
          time: '10:00-12:00',
          status: 'pending'
        },
        {
          id: 'SCH003',
          title: '内容创作计划',
          description: '制定本周内容创作计划',
          streamerName: '小华',
          streamerId: 'U003',
          streamerAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
          type: 'content',
          date: '2024-01-15',
          time: '09:00-11:00',
          status: 'confirmed'
        }
      ]
    }
  },
  methods: {
    getTypeText(type) {
      const typeMap = {
        'live': '直播',
        'content': '内容',
        'activity': '活动'
      }
      return typeMap[type] || type
    },
    getStatusText(status) {
      const statusMap = {
        'confirmed': '已确认',
        'pending': '待审核',
        'cancelled': '已取消'
      }
      return statusMap[status] || status
    }
  }
}
</script>

<style scoped>
.schedule {
  background: #f5f5f5;
  min-height: calc(100vh - 64px);
}

.grid {
  display: grid;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}

@media (min-width: 768px) {
  .md\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .lg\:grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  
  .lg\:grid-cols-7 {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
}

.gap-1 {
  gap: 0.25rem;
}

.gap-6 {
  gap: 1.5rem;
}

.mb-8 {
  margin-bottom: 2rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.text-2xl {
  font-size: 1.5rem;
}

.text-xl {
  font-size: 1.25rem;
}

.text-sm {
  font-size: 0.875rem;
}

.text-xs {
  font-size: 0.75rem;
}

.font-bold {
  font-weight: 700;
}

.font-medium {
  font-weight: 500;
}

.text-gray-500 {
  color: #6b7280;
}

.text-gray-600 {
  color: #4b5563;
}

.text-gray-700 {
  color: #374151;
}

.text-gray-900 {
  color: #111827;
}

.text-blue-600 {
  color: #2563eb;
}

.text-green-600 {
  color: #16a34a;
}

.text-yellow-600 {
  color: #ca8a04;
}

.text-purple-600 {
  color: #9333ea;
}

.text-red-600 {
  color: #dc2626;
}

.bg-blue-100 {
  background-color: #dbeafe;
}

.bg-green-100 {
  background-color: #dcfce7;
}

.bg-yellow-100 {
  background-color: #fef3c7;
}

.bg-purple-100 {
  background-color: #f3e8ff;
}

.bg-red-100 {
  background-color: #fee2e2;
}

.bg-blue-600 {
  background-color: #2563eb;
}

.bg-gray-100 {
  background-color: #f3f4f6;
}

.bg-gray-50 {
  background-color: #f9fafb;
}

.text-blue-800 {
  color: #1e40af;
}

.text-green-800 {
  color: #166534;
}

.text-yellow-800 {
  color: #92400e;
}

.text-red-800 {
  color: #991b1b;
}

.text-white {
  color: #ffffff;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.rounded-full {
  border-radius: 9999px;
}

.px-3 {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.px-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}

.px-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.py-1 {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.py-3 {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.py-4 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.p-6 {
  padding: 1.5rem;
}

.p-3 {
  padding: 0.75rem;
}

.p-2 {
  padding: 0.5rem;
}

.ml-4 {
  margin-left: 1rem;
}

.ml-3 {
  margin-left: 0.75rem;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}

.space-x-2 > * + * {
  margin-left: 0.5rem;
}

.space-y-1 > * + * {
  margin-top: 0.25rem;
}

.relative {
  position: relative;
}

.w-full {
  width: 100%;
}

.min-h-24 {
  min-height: 6rem;
}

.h-8 {
  height: 2rem;
}

.w-8 {
  width: 2rem;
}

.object-cover {
  object-fit: cover;
}

.overflow-hidden {
  overflow: hidden;
}

.overflow-x-auto {
  overflow-x: auto;
}

.shadow-md {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.border {
  border-width: 1px;
}

.border-b {
  border-bottom-width: 1px;
}

.border-gray-200 {
  border-color: #e5e7eb;
}

.divide-y > * + * {
  border-top-width: 1px;
}

.divide-gray-200 > * + * {
  border-color: #e5e7eb;
}

.focus\:outline-none:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
}

.hover\:bg-blue-700:hover {
  background-color: #1d4ed8;
}

.hover\:bg-gray-200:hover {
  background-color: #e5e7eb;
}

.hover\:bg-gray-50:hover {
  background-color: #f9fafb;
}

.hover\:text-blue-900:hover {
  color: #1e40af;
}

.hover\:text-green-900:hover {
  color: #166534;
}

.hover\:text-red-900:hover {
  color: #7f1d1d;
}

.whitespace-nowrap {
  white-space: nowrap;
}

.uppercase {
  text-transform: uppercase;
}

.tracking-wider {
  letter-spacing: 0.05em;
}

.text-center {
  text-align: center;
}

.cursor-pointer {
  cursor: pointer;
}
</style> 